// Original source is https://www.npmjs.com/package/uint8array-json-parser
// Slightly modified

/*
 * MIT License
 *
 * Copyright (c) 2017 Evan Wallace
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

enum State {
  TopLevel,
  Array,
  Object,
}

enum Char {
  // Punctuation
  Newline = 0x0a,
  Space = 0x20,
  Quote = 0x22,
  Plus = 0x2b,
  Comma = 0x2c,
  Minus = 0x2d,
  Dot = 0x2e,
  Slash = 0x2f,
  Colon = 0x3a,
  Equal = 0x3d,
  OpenBracket = 0x5b,
  Backslash = 0x5c,
  CloseBracket = 0x5d,
  OpenBrace = 0x7b,
  CloseBrace = 0x7d,

  // Numbers
  Digit0 = 0x30,
  Digit1 = 0x31,
  Digit2 = 0x32,
  Digit3 = 0x33,
  Digit4 = 0x34,
  Digit5 = 0x35,
  Digit6 = 0x36,
  Digit7 = 0x37,
  Digit8 = 0x38,
  Digit9 = 0x39,

  // Uppercase letters
  UpperA = 0x41,
  UpperE = 0x45,
  UpperF = 0x46,

  // Lowercase letters
  LowerA = 0x61,
  LowerB = 0x62,
  LowerE = 0x65,
  LowerF = 0x66,
  LowerL = 0x6c,
  LowerN = 0x6e,
  LowerR = 0x72,
  LowerS = 0x73,
  LowerT = 0x74,
  LowerU = 0x75,
}

const fromCharCode = String.fromCodePoint;

function throwSyntaxError(bytes: Uint8Array, index: number, message?: string): void {
  const c = bytes[index];
  let line = 1;
  let column = 0;

  for (let i = 0; i < index; i++) {
    if (bytes[i] === Char.Newline) {
      line++;
      column = 0;
    } else {
      column++;
    }
  }

  throw new SyntaxError(
    index === bytes.length
      ? `${message}\nUnexpected end of input while parsing JSON (line ${line}, column ${column}).`
      : c >= 0x20 && c <= 0x7e
        ? `${message}\nUnexpected character ${fromCharCode(
            c,
          )} (char code: ${c}) in JSON at position ${index} (line ${line}, column ${column})`
        : `${message}\nUnexpected byte 0x${c.toString(
            16,
          )} in JSON at position ${index} (line ${line}, column ${column})`,
  );
}

export type Config = {
  // Fix: Data Provider Facebook sends some UTF-8 encoded strings
  decodeStringsAsUtf8?: boolean;
  // Fix: Data Provider Twitter sends js instead of json files and we skip the "window.YTD.xxx.part0 = " part
  skipUntilEqualSign?: boolean;
};

const defaultSettings: Config = {
  decodeStringsAsUtf8: false,
  skipUntilEqualSign: false,
};

export default class Uint8ToObj {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static parse(bytes: Uint8Array, customConfig: Config = defaultSettings): any {
    const config = { ...defaultSettings, ...customConfig };
    const propertyStack: (string | null)[] = [];
    const objectStack: (Record<string, unknown> | undefined | unknown[])[] = [];
    const stateStack: State[] = [];
    const length = bytes.length;
    let property: string | null = null;
    let state = State.TopLevel;
    let object: Record<string, unknown> | undefined | unknown[];
    let i = 0;

    if (config.skipUntilEqualSign) {
      while (i < length) {
        const c = bytes[i++];

        // Skip whitespace
        if (c === Char.Equal) {
          break;
        }
      }
    }

    while (i < length) {
      let c = bytes[i++];

      // Skip whitespace
      if (c <= Char.Space) {
        continue;
      }

      let value: number | boolean | string | unknown[] | null | undefined | Record<string, unknown>;

      // Validate state inside objects
      if (state === State.Object && property === null && c !== Char.Quote && c !== Char.CloseBrace) {
        throwSyntaxError(bytes, --i, "Validate state inside objects");
      }

      switch (c) {
        // True
        case Char.LowerT: {
          if (bytes[i++] !== Char.LowerR || bytes[i++] !== Char.LowerU || bytes[i++] !== Char.LowerE) {
            throwSyntaxError(bytes, --i, "Expected True");
          }

          value = true;
          break;
        }

        // False
        case Char.LowerF: {
          if (
            bytes[i++] !== Char.LowerA ||
            bytes[i++] !== Char.LowerL ||
            bytes[i++] !== Char.LowerS ||
            bytes[i++] !== Char.LowerE
          ) {
            throwSyntaxError(bytes, --i, "Expected False");
          }

          value = false;
          break;
        }

        // Null
        case Char.LowerN: {
          if (bytes[i++] !== Char.LowerU || bytes[i++] !== Char.LowerL || bytes[i++] !== Char.LowerL) {
            throwSyntaxError(bytes, --i, "Expected Null");
          }

          value = null;
          break;
        }

        // Number begin
        case Char.Minus:
        case Char.Dot:
        case Char.Digit0:
        case Char.Digit1:
        case Char.Digit2:
        case Char.Digit3:
        case Char.Digit4:
        case Char.Digit5:
        case Char.Digit6:
        case Char.Digit7:
        case Char.Digit8:
        case Char.Digit9: {
          let index = i;
          value = fromCharCode(c);
          c = bytes[i];

          // Scan over the rest of the number
          for (;;) {
            switch (c) {
              case Char.Plus:
              case Char.Minus:
              case Char.Dot:
              case Char.Digit0:
              case Char.Digit1:
              case Char.Digit2:
              case Char.Digit3:
              case Char.Digit4:
              case Char.Digit5:
              case Char.Digit6:
              case Char.Digit7:
              case Char.Digit8:
              case Char.Digit9:
              case Char.LowerE:
              case Char.UpperE: {
                value += fromCharCode(c);
                c = bytes[++i];
                continue;
              }
            }

            // Number end
            break;
          }

          // Convert the string to a number
          value = +value;

          // Validate the number
          if (isNaN(value)) {
            throwSyntaxError(bytes, --index, "Invalid number");
          }

          break;
        }

        // String begin
        case Char.Quote: {
          value = "";

          for (;;) {
            if (i >= length) {
              throwSyntaxError(bytes, length, "Invalid length of string");
            }

            c = bytes[i++];

            // String end
            if (c === Char.Quote) {
              if (config.decodeStringsAsUtf8) {
                value = decodeURIComponent(escape(value));
              }
              break;
            } else if (c === Char.Backslash) {
              // Escape sequence
              switch (bytes[i++]) {
                // Normal escape sequence
                // eslint-disable-next-line no-useless-escape
                case Char.Quote:
                  value += '"';
                  break;
                // eslint-disable-next-line no-useless-escape
                case Char.Slash:
                  value += "/";
                  break;
                case Char.Backslash:
                  value += "\\";
                  break;
                case Char.LowerB:
                  value += "\b";
                  break;
                case Char.LowerF:
                  value += "\f";
                  break;
                case Char.LowerN:
                  value += "\n";
                  break;
                case Char.LowerR:
                  value += "\r";
                  break;
                case Char.LowerT:
                  value += "\t";
                  break;

                // Unicode escape sequence
                case Char.LowerU: {
                  let code = 0;
                  for (let j = 0; j < 4; j++) {
                    c = bytes[i++];
                    code <<= 4;
                    if (c >= Char.Digit0 && c <= Char.Digit9) code |= c - Char.Digit0;
                    else if (c >= Char.LowerA && c <= Char.LowerF) code |= c + (10 - Char.LowerA);
                    else if (c >= Char.UpperA && c <= Char.UpperF) code |= c + (10 - Char.UpperA);
                    else throwSyntaxError(bytes, --i, "Could not escape Unicode sequence");
                  }
                  value += fromCharCode(code);
                  break;
                }

                // Invalid escape sequence
                default:
                  throwSyntaxError(bytes, --i, "Invalid escape sequence");
                  break;
              }
            } else if (c <= 0x7f) {
              // ASCII text
              value += fromCharCode(c);
            } else if ((c & 0xe0) === 0xc0) {
              // 2-byte UTF-8 sequence
              value += fromCharCode(((c & 0x1f) << 6) | (bytes[i++] & 0x3f));
            } else if ((c & 0xf0) === 0xe0) {
              // 3-byte UTF-8 sequence
              value += fromCharCode(((c & 0x0f) << 12) | ((bytes[i++] & 0x3f) << 6) | (bytes[i++] & 0x3f));
            } else if ((c & 0xf8) === 0xf0) {
              // 4-byte UTF-8 sequence
              let codePoint =
                ((c & 0x07) << 18) | ((bytes[i++] & 0x3f) << 12) | ((bytes[i++] & 0x3f) << 6) | (bytes[i++] & 0x3f);
              if (codePoint > 0xffff) {
                codePoint -= 0x10000;
                value += fromCharCode(((codePoint >> 10) & 0x3ff) | 0xd800);
                codePoint = 0xdc00 | (codePoint & 0x3ff);
              }
              value += fromCharCode(codePoint);
            }
          }

          // Force V8's rope representation to be flattened to compact the string and avoid running out of memory
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          value[0];
          break;
        }

        // Array begin
        case Char.OpenBracket: {
          value = [];

          // Push the stack
          propertyStack.push(property);
          objectStack.push(object);
          stateStack.push(state);

          // Enter the array
          property = null;
          object = value;
          state = State.Array;
          continue;
        }

        // Object begin
        case Char.OpenBrace: {
          value = {};

          // Push the stack
          propertyStack.push(property);
          objectStack.push(object);
          stateStack.push(state);

          // Enter the object
          property = null;
          object = value;
          state = State.Object;
          continue;
        }

        // Array end
        case Char.CloseBracket: {
          if (state !== State.Array) {
            throwSyntaxError(bytes, --i, "Expected Close Bracket of Array");
          }

          // Leave the array
          value = object;

          // Pop the stack
          property = propertyStack.pop() as string | null;
          object = objectStack.pop();
          state = stateStack.pop() as State;
          break;
        }

        // Object end
        case Char.CloseBrace: {
          if (state !== State.Object) {
            throwSyntaxError(bytes, --i, "Expected CloseBrace for Object");
          }

          // Leave the object
          value = object;

          // Pop the stack
          property = propertyStack.pop() as string | null;
          object = objectStack.pop();
          state = stateStack.pop() as State;
          break;
        }

        default: {
          throwSyntaxError(bytes, --i, "Something todo with Objects");
        }
      }

      c = bytes[i];

      // Skip whitespace
      while (c <= Char.Space) {
        c = bytes[++i];
      }

      switch (state) {
        case State.TopLevel: {
          // Expect the end of the input
          if (i === length) {
            return value;
          }

          break;
        }

        case State.Array: {
          // Type checking
          (object as unknown[]).push(value);

          // Check for more values
          if (c === Char.Comma) {
            i++;
            continue;
          }

          // Expect the end of the array
          if (c === Char.CloseBracket) {
            continue;
          }

          break;
        }

        case State.Object: {
          // Property
          if (property === null) {
            // Type checking
            property = value as string | null;

            // Expect a colon
            if (c === Char.Colon) {
              i++;
              continue;
            }
          } else {
            // Value
            // Type checking
            (object as Record<string, unknown>)[property] = value;
            property = null;

            // Check for more values
            if (c === Char.Comma) {
              i++;
              continue;
            }

            // Expect the end of the object
            if (c === Char.CloseBrace) {
              continue;
            }
          }

          break;
        }
      }

      // It's an error if we get here
      break;
    }

    throwSyntaxError(bytes, i, `It's an error if we get here. (${state}, ${property}, ${object}, ${state})`);
    return;
  }
}
